// DashboardStats - KPI Widgets
import { SimpleGrid, Paper, Text, Group, RingProgress, ThemeIcon } from '@mantine/core';
import {
  IconUsers,
  IconPhone,
  IconCheck,
  IconTrendingUp,
  IconStar,
} from '@tabler/icons-react';
import type { LeadStats } from '@/types/models/lead.types';

interface DashboardStatsProps {
  stats: LeadStats;
  isLoading?: boolean;
}

export const DashboardStats = ({ stats, isLoading }: DashboardStatsProps) => {
  // Provide default values if stats is undefined
  const safeStats = stats || {
    total_leads: 0,
    new_leads: 0,
    contacted_leads: 0,
    converted_leads: 0,
    conversion_rate: 0,
    average_score: 0,
  };

  const statCards = [
    {
      title: 'Total Leads',
      value: safeStats.total_leads,
      icon: IconUsers,
      color: 'blue',
    },
    {
      title: 'New Leads',
      value: safeStats.new_leads,
      icon: IconStar,
      color: 'yellow',
    },
    {
      title: 'Contacted',
      value: safeStats.contacted_leads,
      icon: IconPhone,
      color: 'cyan',
    },
    {
      title: 'Converted',
      value: safeStats.converted_leads,
      icon: IconCheck,
      color: 'green',
    },
    {
      title: 'Conversion Rate',
      value: `${safeStats.conversion_rate.toFixed(1)}%`,
      icon: IconTrendingUp,
      color: 'grape',
      showProgress: true,
      progressValue: safeStats.conversion_rate,
    },
    {
      title: 'Avg Score',
      value: safeStats.average_score,
      icon: IconStar,
      color: 'orange',
      showProgress: true,
      progressValue: safeStats.average_score,
    },
  ];

  return (
    <SimpleGrid cols={{ base: 1, sm: 2, md: 3, lg: 6 }} spacing="md">
      {statCards.map((stat) => {
        const Icon = stat.icon;
        return (
          <Paper key={stat.title} withBorder p="md" radius="md">
            <Group justify="space-between">
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                  {stat.title}
                </Text>
                <Text fw={700} size="xl" mt="xs">
                  {isLoading ? '...' : stat.value}
                </Text>
              </div>
              {stat.showProgress ? (
                <RingProgress
                  size={80}
                  roundCaps
                  thickness={8}
                  sections={[{ value: stat.progressValue as number, color: stat.color }]}
                  label={
                    <Text c={stat.color} fw={700} ta="center" size="xs">
                      {stat.progressValue}
                    </Text>
                  }
                />
              ) : (
                <ThemeIcon color={stat.color} variant="light" size={50} radius="md">
                  <Icon size={28} stroke={1.5} />
                </ThemeIcon>
              )}
            </Group>
          </Paper>
        );
      })}
    </SimpleGrid>
  );
};
